import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { contactForm, companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    role: '',
    areaOfInterest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.areaOfInterest) newErrors.areaOfInterest = 'Please select an area of interest';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        role: formData.role || null,
        area_of_interest: formData.areaOfInterest,
        message: formData.message || null,
      });
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        role: '',
        areaOfInterest: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24">
        <section className="py-32 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
          <div className="container-custom">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-[#1A1A1A] mb-4">
                Thank You!
              </h1>
              <p className="text-[#6B7280] mb-8">
                We've received your message and will get back to you within 
                1-2 business days.
              </p>
              <Button
                onClick={() => setIsSubmitted(false)}
                className="bg-[#F58B23] hover:bg-[#D97706] text-white"
              >
                Send Another Message
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#1A1A1A] mb-6">
              {contactForm.title}
            </h1>
            <p className="text-lg text-[#6B7280] leading-relaxed">
              {contactForm.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-6">
                Contact  - TekClap Global HQ
              </h3>
              <div className="space-y-6">
                <a
                  href={`mailto:${companyInfo.contact.email}`}
                  className="flex items-center gap-4 text-[#6B7280] hover:text-[#F58B23] transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center group-hover:bg-[#F58B23] transition-colors">
                    <Mail className="w-5 h-5 text-[#F58B23] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="text-sm text-[#9CA3AF]">Email</div>
                    <div className="font-medium">{companyInfo.contact.email}</div>
                  </div>
                </a>
                <a
                  href={`tel:${companyInfo.contact.phone.replace(/-/g, '')}`}
                  className="flex items-center gap-4 text-[#6B7280] hover:text-[#F58B23] transition-colors group"
                >
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center group-hover:bg-[#F58B23] transition-colors">
                    <Phone className="w-5 h-5 text-[#F58B23] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="text-sm text-[#9CA3AF]">Phone</div>
                    <div className="font-medium">{companyInfo.contact.phone}</div>
                  </div>
                </a>
                <div className="flex items-start gap-4 text-[#6B7280]">
                  <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#F58B23]" />
                  </div>
                  <div>
                    <div className="text-sm text-[#9CA3AF]">Address</div>
                    <div className="font-medium">{companyInfo.contact.address}</div>
                  </div>
                </div>
              </div>

              {/* Strategic Partner */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-[#1A1A1A] mb-5">
                  Strategic Partner - TekClap 365 Pvt Ltd.
                </h4>
                <div className="space-y-4">
                  <a
                    href="mailto:care@tekclap365.com"
                    className="flex items-center gap-4 text-[#6B7280] hover:text-[#F58B23] transition-colors group"
                  >
                    <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center group-hover:bg-[#F58B23] transition-colors">
                      <Mail className="w-5 h-5 text-[#F58B23] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <div className="text-sm text-[#9CA3AF]">Email</div>
                      <div className="font-medium">care@tekclap365.com</div>
                    </div>
                  </a>
                  <a
                    href="tel:+919818341277"
                    className="flex items-center gap-4 text-[#6B7280] hover:text-[#F58B23] transition-colors group"
                  >
                    <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center group-hover:bg-[#F58B23] transition-colors">
                      <Phone className="w-5 h-5 text-[#F58B23] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <div className="text-sm text-[#9CA3AF]">Phone</div>
                      <div className="font-medium">+91-9818341277</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 text-[#6B7280]">
                    <div className="w-12 h-12 bg-[#FFF4E6] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#F58B23]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#9CA3AF]">Address</div>
                      <div className="font-medium text-sm leading-relaxed">
                        Levana Cyberheights, 518 Vibhuti Khand,<br />
                        Gomti Nagar, Lucknow, UP - 226010
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1A1A1A] font-medium text-sm">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23] ${
                        errors.name ? 'border-red-500' : ''
                      }`}
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm">{errors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-[#1A1A1A] font-medium text-sm">
                      Company <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      className={`border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23] ${
                        errors.company ? 'border-red-500' : ''
                      }`}
                      placeholder="Your company"
                    />
                    {errors.company && (
                      <p className="text-red-500 text-sm">{errors.company}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1A1A1A] font-medium text-sm">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23] ${
                        errors.email ? 'border-red-500' : ''
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm">{errors.email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-[#1A1A1A] font-medium text-sm">
                      Role
                    </Label>
                    <Input
                      id="role"
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                      className="border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23]"
                      placeholder="Your role"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="areaOfInterest" className="text-[#1A1A1A] font-medium text-sm">
                    Area of Interest <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.areaOfInterest}
                    onValueChange={(value) => handleChange('areaOfInterest', value)}
                  >
                    <SelectTrigger
                      className={`border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23] ${
                        errors.areaOfInterest ? 'border-red-500' : ''
                      }`}
                    >
                      <SelectValue placeholder="Select an area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technology Consulting">
                        Technology Consulting
                      </SelectItem>
                      <SelectItem value="Product & AI Solutions">
                        Product & AI Solutions
                      </SelectItem>
                      <SelectItem value="Talent & Workforce Solutions">
                        Talent & Workforce Solutions
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.areaOfInterest && (
                    <p className="text-red-500 text-sm">{errors.areaOfInterest}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#1A1A1A] font-medium text-sm">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="border-gray-200 focus:border-[#F58B23] focus:ring-[#F58B23] min-h-[120px]"
                    placeholder="Tell us about your project or needs..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base w-full md:w-auto"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Message
                      <Send className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
